import React, { useEffect, useRef } from 'react';
import { deriveLidAantalFromStatus } from '../config/ledenUploadTemplate';
import { formatGemeenschapLabel } from '../services/conviventieLabels';
import './ConviventieCard.css';

const getLidAantal = (lid) => {
  const providedAantal = Number(lid?.aantal);
  if (!Number.isNaN(providedAantal) && providedAantal > 0) {
    return providedAantal;
  }

  return deriveLidAantalFromStatus(lid?.status);
};

const isLedenlijstLoaded = (uploadStatus) => (
  uploadStatus === 'uploaded' || uploadStatus === 'confirmed'
);

/**
 * Feature 41: reveal the panel that just opened below the card's columns, which on
 * a small screen (or with a long invitation list) lands outside the viewport.
 *
 * `block: 'nearest'` keeps the movement minimal: an already visible panel does not
 * move at all, a short panel is revealed in full, and a tall panel stays reachable
 * from its top. Users who asked for reduced motion get the reveal without the
 * animation, and the method is guarded because jsdom and very old browsers do not
 * implement `scrollIntoView`.
 */
const scrollPanelIntoView = (element) => {
  if (typeof element?.scrollIntoView !== 'function') return;

  const prefersReducedMotion = (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  element.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'nearest'
  });
};

const ConviventieCard = ({
  conviventie,
  canExport,
  onExport,
  canConfirmGemeenschap,
  canManageGemeenschapUpload,
  onConfirmGemeenschap,
  onManageGemeenschapUpload,
  canConfirmGuestGroup,
  onConfirmGuestGroup,
  onManageGuestGroupUpload,
  activePanelTarget,
  canEdit,
  onEdit,
  onDelete,
  children
}) => {
  const uitnodigingen = Array.isArray(conviventie.uitgenodigdeGemeenschappen)
    ? conviventie.uitgenodigdeGemeenschappen
    : [];

  const invitedGemeenschapCount = uitnodigingen.length;
  const invitedGuestGroups = Array.isArray(conviventie.invitedGuestGroups)
    ? conviventie.invitedGuestGroups
    : [];

  // Feature 38: the row action whose panel (confirm or upload handoff) is currently
  // open gets a pressed look, so users can see which target they are working on.
  const isPanelOpenFor = (targetType, targetId) => (
    !!activePanelTarget &&
    activePanelTarget.targetType === targetType &&
    activePanelTarget.targetId === targetId
  );

  // Feature 41: the panel renders in the slot below the columns, so scroll it into
  // view once it opens. Keying the effect on the open target means switching to
  // another row of this card scrolls again, while closing the panel never scrolls.
  // The page already scopes `activePanelTarget` to the card that owns the panel.
  const panelSlotRef = useRef(null);
  const openPanelKey = activePanelTarget
    ? `${activePanelTarget.targetType}:${activePanelTarget.targetId}`
    : '';

  useEffect(() => {
    if (!openPanelKey) return;
    scrollPanelIntoView(panelSlotRef.current);
  }, [openPanelKey]);

  const confirmedGemeenschapCount = uitnodigingen.reduce((sum, gemeenschap) => {
    const leden = Array.isArray(gemeenschap.bevestigdeLeden) ? gemeenschap.bevestigdeLeden : [];
    const confirmedGemeenschapLedenCount = leden.reduce(
      (ledenSum, lid) => ledenSum + getLidAantal(lid),
      0
    );
    return sum + confirmedGemeenschapLedenCount;
  }, 0);

  const confirmedGuestGroupCount = invitedGuestGroups.reduce((sum, guestGroup) => {
    const leden = Array.isArray(guestGroup.bevestigdeLeden) ? guestGroup.bevestigdeLeden : [];
    const confirmedGuestGroupLedenCount = leden.reduce(
      (ledenSum, lid) => ledenSum + getLidAantal(lid),
      0
    );
    return sum + confirmedGuestGroupLedenCount;
  }, 0);

  return (
    <div className="conviventie-card">
      <div className="conviventie-card-head">
        <div className="col-head">Gegevens</div>
        <div className="col-head">Uitgenodigde gemeenschappen</div>
        <div className="col-head">Bevestigd</div>
      </div>

      <div className="conviventie-card-columns">
        <div className="conviventie-col main-details">
          <div className="cell-badge">{conviventie.naam}</div>
          <div className="cell-primary">Start datum: {conviventie.datum}</div>
          <div className="cell-primary">Eind datum: {conviventie.eindDatum || '-'}</div>
          <div className="cell-secondary">Locatie: {conviventie.locatie || '-'}</div>
          <div className="cell-secondary">Uitgenodigd: {invitedGemeenschapCount} gemeenschappen</div>
          <div className="cell-secondary">Gastengroepen: {invitedGuestGroups.length}</div>
          <div className="cell-secondary strong">Totaal bevestigd: {confirmedGemeenschapCount + confirmedGuestGroupCount} leden</div>
        </div>

        <div className="conviventie-col invited-status-col">
          {uitnodigingen.length === 0 && invitedGuestGroups.length === 0 ? (
            <p className="muted">-</p>
          ) : (
            <>
              {uitnodigingen.length > 0 && (
                <ul className="community-list invited-grid">
                  {uitnodigingen.map((gemeenschap) => (
                    <li key={gemeenschap.gemeenschapId} className="community-row">
                      <span className="cell-primary community-name">{formatGemeenschapLabel(gemeenschap)}</span>
                      <div className="community-count-actions">
                        <span className="community-count">
                          {(Array.isArray(gemeenschap.bevestigdeLeden) ? gemeenschap.bevestigdeLeden : []).reduce(
                            (sum, lid) => sum + getLidAantal(lid),
                            0
                          )}
                        </span>
                        {canManageGemeenschapUpload && canManageGemeenschapUpload(gemeenschap) && !isLedenlijstLoaded(gemeenschap.uploadStatus) && (
                          <button
                            className={`confirm-row-btn upload-row-btn${isPanelOpenFor('gemeenschap', gemeenschap.gemeenschapId) ? ' is-pressed' : ''}`}
                            onClick={() => onManageGemeenschapUpload(gemeenschap)}
                          >
                            Ledenlijst uploaden
                          </button>
                        )}
                        {canConfirmGemeenschap(gemeenschap) && isLedenlijstLoaded(gemeenschap.uploadStatus) && (
                          <button
                            className={`confirm-row-btn${isPanelOpenFor('gemeenschap', gemeenschap.gemeenschapId) ? ' is-pressed' : ''}`}
                            onClick={() => onConfirmGemeenschap(gemeenschap)}
                          >
                            Bevestig deelname
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {invitedGuestGroups.length > 0 && (
                <ul className="community-list invited-grid guest-group-list">
                  {invitedGuestGroups.map((guestGroup) => (
                    <li key={guestGroup.guestGroupId} className="community-row">
                      <span className="cell-primary community-name guest-group-name">{guestGroup.guestGroupName || '-'}</span>
                      <div className="community-count-actions">
                        <span className="community-count">
                          {(Array.isArray(guestGroup.bevestigdeLeden) ? guestGroup.bevestigdeLeden : []).reduce(
                            (sum, lid) => sum + getLidAantal(lid),
                            0
                          )}
                        </span>
                        {onManageGuestGroupUpload && canConfirmGuestGroup && canConfirmGuestGroup(guestGroup) && !isLedenlijstLoaded(guestGroup.uploadStatus) && (
                          <button
                            className={`confirm-row-btn upload-row-btn${isPanelOpenFor('guestGroup', guestGroup.guestGroupId) ? ' is-pressed' : ''}`}
                            onClick={() => onManageGuestGroupUpload(guestGroup)}
                          >
                            Ledenlijst uploaden
                          </button>
                        )}
                        {canConfirmGuestGroup && canConfirmGuestGroup(guestGroup) && isLedenlijstLoaded(guestGroup.uploadStatus) && (
                          <button
                            className={`confirm-row-btn${isPanelOpenFor('guestGroup', guestGroup.guestGroupId) ? ' is-pressed' : ''}`}
                            onClick={() => onConfirmGuestGroup(guestGroup)}
                          >
                            Bevestig gasten
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      <div className="conviventie-card-actions">
        {canExport && (
          <button className="button button-secondary" onClick={() => onExport(conviventie)}>
            Exporteren (Excel)
          </button>
        )}
        {canEdit && (
          <>
            <button className="button button-secondary" onClick={() => onEdit(conviventie)}>
              Bewerken
            </button>
            <button className="button button-danger" onClick={() => onDelete(conviventie.id)}>
              Verwijderen
            </button>
          </>
        )}
      </div>

      {children && (
        <div className="conviventie-inline-slot" ref={panelSlotRef}>
          {children}
        </div>
      )}
    </div>
  );
};

export default ConviventieCard;
